import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Tab,
  Table,
  Tabs,
} from 'react-bootstrap';
import applicationDetailSignal from '../ApplicationDetail.signal';
import {
  fetchApplicationDetails,
  updateCspInfo,
  createCspInfo,
  createPayment,
  updatePayment,
  deletePayment,
} from '../ApplicationDetail.helpers';
import loaderSignal from 'signals/Loader.signal';
import alertSignal from 'signals/Alert.signal';
import CloseModalButton from 'components/global/Constant/CloseModalButton';

/** Payment from API (amount fields in dollars) */
type PaymentApi = {
  id: number;
  csiId?: number | null;
  paymentType?: string | null;
  transactionDate?: string | null;
  generalSupportAmount?: number | null;
  sacSupportAmount?: number | null;
  fee?: number | null;
  reason?: string | null;
  reasonCode?: string | null;
};

/** Raw CspInfo from API (amount fields in dollars via monetary extension; termsSupport in months) */
type CspInfoApi = {
  id: number;
  csiId?: number | null;
  applicationId: number;
  loanCategory?: string | null;
  loanCategoryLabel?: string | null;
  chargeOffDate?: string | null;
  createdDate?: string | null;
  generalSupportAmount?: number | null;
  generalSupportPercent?: number | null;
  sacSupportAmount?: number | null;
  sacSupportPercent?: number | null;
  totalSupportAmount?: number | null;
  totalSupportPercent?: number | null;
  additionalParameters?: string | null;
  minReqGenRecap?: number | null;
  minReqSacRecap?: number | null;
  annualFee?: number | null;
  comments?: string | null;
  termsSupport?: number | null;
  payments?: PaymentApi[];
};

/** Draft row for form (amounts in dollars for display) */
type CspInfoDraft = {
  id: number;
  csiId: string;
  loanCategory: string;
  loanCategoryLabel: string;
  chargeOffDate: string;
  createdDate: string;
  generalSupportAmount: number | '';
  generalSupportPercent: string;
  sacSupportAmount: number | '';
  sacSupportPercent: string;
  totalSupportAmount: number | '';
  totalSupportPercent: string;
  additionalParameters: string;
  minReqGenRecap: number | '';
  minReqSacRecap: number | '';
  annualFee: number | '';
  termsSupport: string;
  comments: string;
};

/** Loan category options: value = key (stored in DB), label = display text. Matches backend mapLoanCategoryToLabel. */
const LOAN_CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'B', label: 'Bridge Loan' },
  { value: 'S', label: 'Small Business' },
  { value: 'E', label: 'Energy Efficiency' },
];

/** Payment type options for the New Payment modal dropdown. */
const PAYMENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'REC', label: 'REC' },
  { value: 'REQ', label: 'REQ' },
];

/** CSP reason code options (code + description). */
const REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'L', label: 'Loan Initiation' },
  { value: 'A', label: 'Annual Review' },
  { value: 'D', label: 'Default Notification' },
  { value: 'S', label: 'Support End' },
];

function reasonCodeToDescription(code: string | null | undefined): string {
  if (code == null || code === '') return '';
  const opt = REASON_OPTIONS.find((o) => o.value === code);
  return opt ? opt.label : code;
}

function getTodayLocalDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateForInput(val: string | Date | null | undefined): string {
  if (val == null) return '';
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/** Format date for display (MM/DD/YYYY), same as Loan Tracking Date column */
function formatDateDisplay(val: string | Date | null | undefined): string {
  if (val == null) return '';
  const dateOnly = typeof val === 'string' && val.includes('T') ? val.split('T')[0] : String(val).slice(0, 10);
  const parts = dateOnly.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return '';
  const [year, month, day] = parts;
  const formattedMonth = String(month).padStart(2, '0');
  const formattedDay = String(day).padStart(2, '0');
  return `${formattedMonth}/${formattedDay}/${year}`;
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null || Number.isNaN(val)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
}

/** Amount from API is already in dollars (monetary extension) */
function amountFromApi(value: number | null | undefined): number | '' {
  if (value == null) return '';
  return value;
}

function apiToDraft(row: CspInfoApi): CspInfoDraft {
  return {
    id: row.id,
    csiId: row.csiId != null ? String(row.csiId) : '',
    loanCategory: row.loanCategory ?? '',
    loanCategoryLabel: row.loanCategoryLabel ?? '',
    chargeOffDate: formatDateForInput(row.chargeOffDate),
    createdDate: formatDateForInput(row.createdDate),
    generalSupportAmount: amountFromApi(row.generalSupportAmount),
    generalSupportPercent: row.generalSupportPercent != null ? String(row.generalSupportPercent) : '',
    sacSupportAmount: amountFromApi(row.sacSupportAmount),
    sacSupportPercent: row.sacSupportPercent != null ? String(row.sacSupportPercent) : '',
    totalSupportAmount: amountFromApi(row.totalSupportAmount),
    totalSupportPercent: row.totalSupportPercent != null ? String(row.totalSupportPercent) : '',
    additionalParameters: row.additionalParameters ?? '',
    minReqGenRecap: amountFromApi(row.minReqGenRecap),
    minReqSacRecap: amountFromApi(row.minReqSacRecap),
    annualFee: amountFromApi(row.annualFee),
    termsSupport:
      row.termsSupport != null
        ? String(row.termsSupport > 99 ? Math.round(row.termsSupport / 100) : row.termsSupport)
        : '',
    comments: row.comments ?? '',
  };
}

const ApplicationCspInfoTab: React.FC = () => {
  const { id: applicationId } = applicationDetailSignal.value;
  const { isContentLoading } = loaderSignal.value;
  const [savedRows, setSavedRows] = useState<CspInfoApi[]>([]);
  const [draftRows, setDraftRows] = useState<CspInfoDraft[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [focusedAmountFields, setFocusedAmountFields] = useState<Set<string>>(new Set());
  const [amountFieldRawValues, setAmountFieldRawValues] = useState<Record<string, string>>({});
  const [paymentModalCspInfo, setPaymentModalCspInfo] = useState<CspInfoApi | null>(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    transactionDate: '',
    paymentType: '',
    generalSupportAmount: '' as number | '',
    sacSupportAmount: '' as number | '',
    fee: '' as number | '',
    reasonCode: '',
  });
  const [paymentModalAmountFocused, setPaymentModalAmountFocused] = useState<string | null>(null);
  const [paymentModalAmountRaw, setPaymentModalAmountRaw] = useState<Record<string, string>>({});
  const [editingPayment, setEditingPayment] = useState<PaymentApi | null>(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState<number | null>(null);

  const parseCurrency = useCallback((value: string): number | null => {
    if (!value || value.trim() === '') return null;
    const cleaned = value.replace(/[$,\s]/g, '');
    const parsed = parseFloat(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }, []);

  const loadCspInfos = useCallback(async () => {
    if (!applicationId) return;

    try {
      loaderSignal.update({ isContentLoading: true });
      setLoadError(null);
      const details = await fetchApplicationDetails(applicationId);
      const list: CspInfoApi[] = Array.isArray(details?.cspInfos) ? details.cspInfos : [];
      setSavedRows(list);
      if (list.length > 0) {
        setDraftRows(list.map(apiToDraft));
      } else {
        // Initialize a single blank draft row to allow creating first CspInfo
        setDraftRows([
          {
            id: 0,
            csiId: '',
            loanCategory: '',
            loanCategoryLabel: '',
            chargeOffDate: '',
            createdDate: getTodayLocalDate(),
            generalSupportAmount: '',
            generalSupportPercent: '',
            sacSupportAmount: '',
            sacSupportPercent: '',
            totalSupportAmount: '',
            totalSupportPercent: '',
            additionalParameters: '',
            minReqGenRecap: '',
            minReqSacRecap: '',
            annualFee: '',
            termsSupport: '',
            comments: '',
          },
        ]);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load Collateral Support info';
      setLoadError(message);
      alertSignal.update({
        message,
        variant: 'danger',
        type: 'notification',
      });
    } finally {
      loaderSignal.update({ isContentLoading: false });
    }
  }, [applicationId]);

  useEffect(() => {
    if (applicationId) {
      loadCspInfos();
    }
  }, [applicationId, loadCspInfos]);

  const handleFieldChange = useCallback((rowIndex: number, field: keyof CspInfoDraft, value: string | number | '') => {
    setDraftRows((prev) => {
      const next = [...prev];
      const row = { ...next[rowIndex], [field]: value };
      next[rowIndex] = row;
      return next;
    });
  }, []);

  const hasChanges = useCallback(
    (rowIndex: number): boolean => {
      const draft = draftRows[rowIndex];
      const saved = savedRows[rowIndex];
      if (!draft) return false;

      // New row (no existing CspInfo yet): consider changed if any non-empty field
      if (!saved) {
        const { id, csiId, ...rest } = draft;
        return Object.values(rest).some((value) => {
          if (value === '' || value == null) return false;
          return true;
        });
      }

      const draftAmount = (v: number | ''): number | null => (v === '' ? null : v);
      const toNum = (v: string): number | null => {
        if (v === '') return null;
        const n = parseFloat(v);
        return Number.isNaN(n) ? null : n;
      };

      if (draft.loanCategory !== (saved.loanCategory ?? '')) return true;
      if (draft.loanCategoryLabel !== (saved.loanCategoryLabel ?? '')) return true;
      if (draft.chargeOffDate !== formatDateForInput(saved.chargeOffDate)) return true;
      if (draftAmount(draft.generalSupportAmount) !== (saved.generalSupportAmount ?? null)) return true;
      if (toNum(draft.generalSupportPercent) !== (saved.generalSupportPercent ?? null)) return true;
      if (draftAmount(draft.sacSupportAmount) !== (saved.sacSupportAmount ?? null)) return true;
      if (toNum(draft.sacSupportPercent) !== (saved.sacSupportPercent ?? null)) return true;
      if (draftAmount(draft.totalSupportAmount) !== (saved.totalSupportAmount ?? null)) return true;
      if (toNum(draft.totalSupportPercent) !== (saved.totalSupportPercent ?? null)) return true;
      if (draftAmount(draft.annualFee) !== (saved.annualFee ?? null)) return true;
      if (draft.additionalParameters !== (saved.additionalParameters ?? '')) return true;
      if (draftAmount(draft.minReqGenRecap) !== (saved.minReqGenRecap ?? null)) return true;
      if (draftAmount(draft.minReqSacRecap) !== (saved.minReqSacRecap ?? null)) return true;
      const savedTermsDisplay =
        saved.termsSupport != null
          ? String(saved.termsSupport > 99 ? Math.round(saved.termsSupport / 100) : saved.termsSupport)
          : '';
      if (draft.termsSupport !== savedTermsDisplay) return true;
      if (draft.comments !== (saved.comments ?? '')) return true;
      return false;
    },
    [draftRows, savedRows]
  );

  const buildPayload = useCallback((draft: CspInfoDraft): Record<string, unknown> => {
    const amountToPayload = (v: number | ''): number | null => (v === '' ? null : v);
    const toNum = (v: string): number | null => {
      if (v === '') return null;
      const n = parseFloat(v);
      return Number.isNaN(n) ? null : n;
    };

    const toInt = (v: string): number | null => {
      if (v === '') return null;
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    };

    const payload: Record<string, unknown> = {
      loanCategory: draft.loanCategory || null,
      loanCategoryLabel: draft.loanCategoryLabel || null,
      chargeOffDate: draft.chargeOffDate || null,
      generalSupportAmount: amountToPayload(draft.generalSupportAmount),
      generalSupportPercent: toNum(draft.generalSupportPercent),
      sacSupportAmount: amountToPayload(draft.sacSupportAmount),
      sacSupportPercent: toNum(draft.sacSupportPercent),
      totalSupportAmount: amountToPayload(draft.totalSupportAmount),
      totalSupportPercent: toNum(draft.totalSupportPercent),
      additionalParameters: draft.additionalParameters || null,
      minReqGenRecap: amountToPayload(draft.minReqGenRecap),
      minReqSacRecap: amountToPayload(draft.minReqSacRecap),
      annualFee: amountToPayload(draft.annualFee),
      termsSupport: toInt(draft.termsSupport),
      comments: draft.comments || null,
    };

    if (draft.createdDate) {
      payload.createdDate = draft.createdDate;
    }

    return payload;
  }, []);

  const handleSaveRow = useCallback(
    async (rowIndex: number) => {
      const draft = draftRows[rowIndex];
      const saved = savedRows[rowIndex];
      if (!applicationId || !draft || savingId !== null) return;
      if (!hasChanges(rowIndex)) {
        alertSignal.update({
          message: 'No changes to save',
          variant: 'info',
          type: 'notification',
        });
        return;
      }

      setSavingId(draft.id);
      try {
        loaderSignal.update({ isContentLoading: true });
        const payload = buildPayload(draft);
        if (!saved) {
          await createCspInfo(applicationId, payload as Record<string, any>);
          await loadCspInfos();
        } else {
          const updated = await updateCspInfo(applicationId, draft.id, payload as Record<string, any>);
          setSavedRows((prev) => {
            const next = [...prev];
            next[rowIndex] = { ...saved, ...updated };
            return next;
          });
          setDraftRows((prev) => {
            const next = [...prev];
            next[rowIndex] = apiToDraft({ ...saved, ...updated });
            return next;
          });
        }
        alertSignal.update({
          message: 'Collateral Support info saved successfully',
          variant: 'success',
          type: 'notification',
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to save';
        alertSignal.update({
          message,
          variant: 'danger',
          type: 'notification',
        });
      } finally {
        setSavingId(null);
        loaderSignal.update({ isContentLoading: false });
      }
    },
    [applicationId, draftRows, savedRows, hasChanges, buildPayload, savingId]
  );

  const hasAnyChanges = draftRows.some((_, i) => hasChanges(i));

  const handleOpenAddPayment = useCallback((saved: CspInfoApi) => {
    setEditingPayment(null);
    setPaymentModalCspInfo(saved);
    setPaymentForm({
      transactionDate: getTodayLocalDate(),
      paymentType: '',
      generalSupportAmount: '',
      sacSupportAmount: '',
      fee: '',
      reasonCode: '',
    });
  }, []);

  const handleOpenEditPayment = useCallback((payment: PaymentApi, cspInfo: CspInfoApi) => {
    setEditingPayment(payment);
    setPaymentModalCspInfo(cspInfo);
    setPaymentForm({
      transactionDate: formatDateForInput(payment.transactionDate) || '',
      paymentType: payment.paymentType ?? '',
      generalSupportAmount:
        payment.generalSupportAmount != null ? payment.generalSupportAmount : '',
      sacSupportAmount: payment.sacSupportAmount != null ? payment.sacSupportAmount : '',
      fee: payment.fee != null ? payment.fee : '',
      reasonCode: payment.reasonCode ?? '',
    });
  }, []);

  const handleClosePaymentModal = useCallback(() => {
    setPaymentModalCspInfo(null);
    setEditingPayment(null);
    setPaymentModalAmountFocused(null);
    setPaymentModalAmountRaw({});
  }, []);

  const handleCreatePaymentSubmit = useCallback(async () => {
    if (!applicationId || !paymentModalCspInfo || paymentSubmitting) return;
    const payload = {
      transactionDate: paymentForm.transactionDate || null,
      paymentType: paymentForm.paymentType || null,
      generalSupportAmount:
        paymentForm.generalSupportAmount === ''
          ? null
          : Number(paymentForm.generalSupportAmount),
      sacSupportAmount:
        paymentForm.sacSupportAmount === '' ? null : Number(paymentForm.sacSupportAmount),
      fee: paymentForm.fee === '' ? null : Number(paymentForm.fee),
      reasonCode: paymentForm.reasonCode || null,
    };
    setPaymentSubmitting(true);
    try {
      loaderSignal.update({ isContentLoading: true });
      await createPayment(applicationId, paymentModalCspInfo.id, payload);
      await loadCspInfos();
      handleClosePaymentModal();
      alertSignal.update({
        message: 'Payment created successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create payment';
      alertSignal.update({ message, variant: 'danger', type: 'notification' });
    } finally {
      setPaymentSubmitting(false);
      loaderSignal.update({ isContentLoading: false });
    }
  }, [
    applicationId,
    paymentModalCspInfo,
    paymentSubmitting,
    paymentForm,
    loadCspInfos,
    handleClosePaymentModal,
  ]);

  const handleUpdatePaymentSubmit = useCallback(async () => {
    if (!applicationId || !paymentModalCspInfo || !editingPayment || paymentSubmitting) return;
    const payload = {
      transactionDate: paymentForm.transactionDate || null,
      paymentType: paymentForm.paymentType || null,
      generalSupportAmount:
        paymentForm.generalSupportAmount === ''
          ? null
          : Number(paymentForm.generalSupportAmount),
      sacSupportAmount:
        paymentForm.sacSupportAmount === '' ? null : Number(paymentForm.sacSupportAmount),
      fee: paymentForm.fee === '' ? null : Number(paymentForm.fee),
      reasonCode: paymentForm.reasonCode || null,
    };
    setPaymentSubmitting(true);
    try {
      loaderSignal.update({ isContentLoading: true });
      await updatePayment(applicationId, paymentModalCspInfo.id, editingPayment.id, payload);
      await loadCspInfos();
      handleClosePaymentModal();
      alertSignal.update({
        message: 'Payment updated successfully',
        variant: 'success',
        type: 'notification',
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update payment';
      alertSignal.update({ message, variant: 'danger', type: 'notification' });
    } finally {
      setPaymentSubmitting(false);
      loaderSignal.update({ isContentLoading: false });
    }
  }, [
    applicationId,
    paymentModalCspInfo,
    editingPayment,
    paymentSubmitting,
    paymentForm,
    loadCspInfos,
    handleClosePaymentModal,
  ]);

  const handleDeletePayment = useCallback(
    async (payment: PaymentApi, cspInfo: CspInfoApi) => {
      if (!applicationId) return;
      const confirmed = window.confirm(
        'Are you sure you want to delete this payment? This action cannot be undone.'
      );
      if (!confirmed) return;
      setDeletingPaymentId(payment.id);
      try {
        loaderSignal.update({ isContentLoading: true });
        await deletePayment(applicationId, cspInfo.id, payment.id);
        await loadCspInfos();
        alertSignal.update({
          message: 'Payment deleted successfully',
          variant: 'success',
          type: 'notification',
        });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to delete payment';
        alertSignal.update({ message, variant: 'danger', type: 'notification' });
      } finally {
        setDeletingPaymentId(null);
        loaderSignal.update({ isContentLoading: false });
      }
    },
    [applicationId, loadCspInfos]
  );

  const handleResetAll = useCallback(() => {
    if (savedRows.length > 0) {
      setDraftRows(savedRows.map(apiToDraft));
    } else {
      setDraftRows([
        {
          id: 0,
          csiId: '',
          loanCategory: '',
          loanCategoryLabel: '',
          chargeOffDate: '',
          createdDate: getTodayLocalDate(),
          generalSupportAmount: '',
          generalSupportPercent: '',
          sacSupportAmount: '',
          sacSupportPercent: '',
          totalSupportAmount: '',
          totalSupportPercent: '',
          additionalParameters: '',
          minReqGenRecap: '',
          minReqSacRecap: '',
          annualFee: '',
          termsSupport: '',
          comments: '',
        },
      ]);
    }
  }, [savedRows]);

  const handleSaveAll = useCallback(async () => {
    if (!applicationId || savingId !== null) return;
    const indicesToSave = draftRows.map((_, i) => i).filter((i) => hasChanges(i));
    if (indicesToSave.length === 0) {
      alertSignal.update({
        message: 'No changes to save',
        variant: 'info',
        type: 'notification',
      });
      return;
    }
    for (const rowIndex of indicesToSave) {
      await handleSaveRow(rowIndex);
    }
  }, [applicationId, draftRows, savingId, hasChanges, handleSaveRow]);

  const amountFieldKey = (rowIndex: number, field: string) => `${rowIndex}_${field}`;

  const renderAmountInput = (
    rowIndex: number,
    field: keyof CspInfoDraft,
    label: string,
    asColumn = true
  ) => {
    const draft = draftRows[rowIndex];
    if (!draft) return null;
    const amountValue = draft[field];
    const key = amountFieldKey(rowIndex, field);
    const isFocused = focusedAmountFields.has(key);
    const hasRawValue = amountFieldRawValues[key] !== undefined && amountFieldRawValues[key] !== '';
    const shouldShowRawValue = isFocused && hasRawValue && savingId === null;

    let displayValue: string;
    if (shouldShowRawValue) {
      displayValue = amountFieldRawValues[key] ?? '';
    } else {
      const numValue =
        typeof amountValue === 'number'
          ? amountValue
          : amountValue !== ''
            ? Number(amountValue)
            : null;
      if (numValue !== null && !Number.isNaN(numValue) && isFinite(numValue)) {
        displayValue = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(numValue);
      } else {
        displayValue = '';
      }
    }

    const groupProps = asColumn
      ? { as: Col, md: 6, lg: 4, key: field, className: 'mb-3' as const }
      : { key: field, className: 'mb-3' as const };
    return (
      <Form.Group {...groupProps}>
        <Form.Label>{label}</Form.Label>
        <InputGroup>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            type="text"
            value={displayValue}
            disabled={isContentLoading || savingId !== null}
            onFocus={() => {
              setFocusedAmountFields((prev) => new Set(prev).add(key));
              if (
                amountValue !== null &&
                amountValue !== undefined &&
                amountValue !== '' &&
                !Number.isNaN(Number(amountValue))
              ) {
                const raw =
                  typeof amountValue === 'number'
                    ? amountValue.toFixed(2)
                    : String(amountValue);
                setAmountFieldRawValues((prev) => ({ ...prev, [key]: raw }));
              } else {
                setAmountFieldRawValues((prev) => ({ ...prev, [key]: '' }));
              }
            }}
            onBlur={() => {
              setFocusedAmountFields((prev) => {
                const next = new Set(prev);
                next.delete(key);
                return next;
              });
              const rawValue = amountFieldRawValues[key] ?? '';
              const numValue = parseCurrency(rawValue);
              if (numValue !== null && !Number.isNaN(numValue)) {
                handleFieldChange(rowIndex, field, numValue);
              } else {
                handleFieldChange(rowIndex, field, '');
              }
              setAmountFieldRawValues((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
              });
            }}
            onChange={(e) => {
              const rawValue = e.target.value;
              setAmountFieldRawValues((prev) => ({ ...prev, [key]: rawValue }));
            }}
            placeholder="0.00"
            inputMode="decimal"
          />
        </InputGroup>
      </Form.Group>
    );
  };

  const renderPercentInput = (
    rowIndex: number,
    field: 'generalSupportPercent' | 'sacSupportPercent' | 'totalSupportPercent',
    label: string,
    asColumn = true
  ) => {
    const draft = draftRows[rowIndex];
    if (!draft) return null;
    const rawValue = draft[field];
    let displayValue = '';
    if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
      const num = Number(rawValue);
      if (!Number.isNaN(num)) {
        displayValue = num > 0 && num <= 1 ? String(num * 100) : String(num);
      } else {
        displayValue = String(rawValue);
      }
    }

    const groupProps = asColumn
      ? { as: Col, md: 6, lg: 4, key: field, className: 'mb-3' as const }
      : { key: field, className: 'mb-3' as const };
    return (
      <Form.Group {...groupProps}>
        <Form.Label>{label}</Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            inputMode="decimal"
            value={displayValue}
            disabled={isContentLoading || savingId !== null}
            onChange={(e) => {
              const s = e.target.value.replace(/[%\s]/g, '');
              if (s === '') {
                handleFieldChange(rowIndex, field, '');
                return;
              }
              const parsed = parseFloat(s);
              if (Number.isNaN(parsed)) return;
              handleFieldChange(
                rowIndex,
                field,
                parsed > 1 ? String(parsed / 100) : String(parsed)
              );
            }}
            placeholder="0"
          />
          <InputGroup.Text>%</InputGroup.Text>
        </InputGroup>
      </Form.Group>
    );
  };

  if (loadError) {
    return (
      <Row>
        <Col>
          <Alert variant="danger">{loadError}</Alert>
        </Col>
      </Row>
    );
  }

  return (
    <div>
      {isContentLoading && savedRows.length === 0 && draftRows.length === 0 ? (
        <div className="text-center py-5">
          <div>Loading Collateral Support info...</div>
        </div>
      ) : (
        <Tabs defaultActiveKey="info" id="csp-info-subtabs" className="mb-0">
          <Tab eventKey="info" title="Info">
            <Row className="mb-4 pt-4">
              <Col>
                <h5>Collateral Support Info</h5>
                <p className="text-muted mb-0">
                  Edit Collateral Support fields below. Changes will be saved when you click &quot;Save Changes&quot;.
                </p>
              </Col>
              <Col className="text-right">
                <Button
                  variant="secondary"
                  className="rounded mr-2"
                  onClick={handleResetAll}
                  disabled={!hasAnyChanges || isContentLoading || savingId !== null}
                >
                  Reset
                </Button>
                <Button
                  variant="primary"
                  className="rounded"
                  onClick={handleSaveAll}
                  disabled={!hasAnyChanges || isContentLoading || savingId !== null}
                >
                  {savingId !== null ? 'Saving...' : 'Save Changes'}
                </Button>
              </Col>
            </Row>
            {draftRows.map((draft, rowIndex) => (
              <React.Fragment key={draft.id}>
            <Row className={rowIndex > 0 ? 'mt-4' : undefined}>
              <Form.Group as={Col} md={6} lg={4} className="mb-3">
                <Form.Label>Loan Category</Form.Label>
                <Form.Control
                  as="select"
                  value={draft.loanCategory}
                  disabled={isContentLoading || savingId !== null}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDraftRows((prev) => {
                      const current = prev[rowIndex];
                      const option = LOAN_CATEGORY_OPTIONS.find((opt) => opt.value === value);
                      const label = option
                        ? option.label
                        : value
                          ? (current.loanCategoryLabel || value)
                          : '';
                      const next = [...prev];
                      next[rowIndex] = {
                        ...current,
                        loanCategory: value,
                        loanCategoryLabel: label,
                      };
                      return next;
                    });
                  }}
                >
                  <option value="">-- Select Loan Category --</option>
                  {LOAN_CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                  {draft.loanCategory &&
                    !LOAN_CATEGORY_OPTIONS.some((opt) => opt.value === draft.loanCategory) && (
                      <option value={draft.loanCategory}>
                        {draft.loanCategoryLabel || draft.loanCategory}
                      </option>
                    )}
                </Form.Control>
              </Form.Group>
              <Form.Group as={Col} md={6} lg={4} className="mb-3">
                <Form.Label>Default Notification Received</Form.Label>
                <Form.Control
                  type="date"
                  value={draft.chargeOffDate}
                  disabled={isContentLoading || savingId !== null}
                  onChange={(e) => handleFieldChange(rowIndex, 'chargeOffDate', e.target.value)}
                />
              </Form.Group>
              <Form.Group as={Col} md={6} lg={4} className="mb-3">
                <Form.Label>Created Date</Form.Label>
                <Form.Control
                  type="date"
                  readOnly={!!savedRows[rowIndex]}
                  disabled={!!savedRows[rowIndex] || isContentLoading || savingId !== null}
                  value={
                    savedRows[rowIndex]
                      ? formatDateForInput(savedRows[rowIndex]?.createdDate) || ''
                      : draft.createdDate
                  }
                  onChange={(e) => {
                    if (!savedRows[rowIndex]) {
                      handleFieldChange(rowIndex, 'createdDate', e.target.value);
                    }
                  }}
                />
              </Form.Group>
            </Row>
            <Row>
              <Col md={4}>
                {renderAmountInput(rowIndex, 'generalSupportAmount', 'General Support Amount', false)}
                {renderAmountInput(rowIndex, 'sacSupportAmount', 'SAC Support Amount', false)}
                {renderAmountInput(rowIndex, 'totalSupportAmount', 'Total Support Amount', false)}
              </Col>
              <Col md={4}>
                {renderPercentInput(rowIndex, 'generalSupportPercent', 'General Support %', false)}
                {renderPercentInput(rowIndex, 'sacSupportPercent', 'SAC Support %', false)}
                {renderPercentInput(rowIndex, 'totalSupportPercent', 'Total Support %', false)}
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Additional Parameters</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    value={draft.additionalParameters}
                    disabled={isContentLoading || savingId !== null}
                    onChange={(e) => handleFieldChange(rowIndex, 'additionalParameters', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {renderAmountInput(rowIndex, 'minReqGenRecap', 'Min. Required General Recap')}
              {renderAmountInput(rowIndex, 'minReqSacRecap', 'Min. Required SAC Recap')}
            </Row>
            <Row>
              {renderAmountInput(rowIndex, 'annualFee', 'Annual Fee')}
              <Form.Group as={Col} md={6} lg={4} className="mb-3">
                <Form.Label>Terms of Support (months)</Form.Label>
                <Form.Control
                  type="text"
                  value={draft.termsSupport}
                  disabled={isContentLoading || savingId !== null}
                  onChange={(e) => handleFieldChange(rowIndex, 'termsSupport', e.target.value)}
                />
              </Form.Group>
              <Form.Group as={Col} md={12} className="mb-3">
                <Form.Label>Comments</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={draft.comments}
                  disabled={isContentLoading || savingId !== null}
                  onChange={(e) => handleFieldChange(rowIndex, 'comments', e.target.value)}
                />
              </Form.Group>
            </Row>
              </React.Fragment>
            ))}
          </Tab>
          <Tab eventKey="payments" title="Payments">
            {savedRows.length === 0 ? (
              <Alert variant="info" className="mb-0 mt-4">
                <p className="mb-0">No Collateral Support info records. Payments are shown per record.</p>
              </Alert>
            ) : (
              <div className="pt-4">
                {savedRows.map((saved, rowIndex) => {
                  const payments: PaymentApi[] = saved.payments ?? [];
                  return (
                    <div key={saved.id} className={rowIndex > 0 ? 'mt-4' : undefined}>
                      <Row className="mb-4">
                        <Col>
                          {rowIndex === 0 ? <h5>Payments Details</h5> : null}
                        </Col>
                        <Col className="text-right">
                          <Button
                            variant="primary"
                            className="rounded"
                            onClick={() => handleOpenAddPayment(saved)}
                          >
                            Add Payment
                          </Button>
                        </Col>
                      </Row>
                    <Table striped bordered hover responsive style={{ tableLayout: 'fixed' }} className="align-top">
                        <thead>
                          <tr>
                            <th style={{ width: '12%' }}>Transaction Date</th>
                            <th style={{ width: '10%' }}>Payment Type</th>
                            <th style={{ width: '14%' }} className="text-end">General Support</th>
                            <th style={{ width: '14%' }} className="text-end">SAC Support</th>
                            <th style={{ width: '12%' }} className="text-end">Fee</th>
                            <th style={{ width: '22%' }}>Reason</th>
                            <th style={{ width: '16%' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center text-muted py-4">
                                No payments for this record. Click &quot;Add Payment&quot; to create one.
                              </td>
                            </tr>
                          ) : (
                            payments.map((p) => (
                              <tr key={p.id}>
                                <td>{formatDateDisplay(p.transactionDate) || <span className="text-muted">—</span>}</td>
                                <td>{p.paymentType ?? <span className="text-muted">—</span>}</td>
                                <td className="text-end">{formatCurrency(p.generalSupportAmount)}</td>
                                <td className="text-end">{formatCurrency(p.sacSupportAmount)}</td>
                                <td className="text-end">{formatCurrency(p.fee)}</td>
                                <td>{(reasonCodeToDescription(p.reasonCode) || p.reason) ?? <span className="text-muted">—</span>}</td>
                                <td>
                                  <span style={{ display: 'flex', gap: '8px' }}>
                                    <Button
                                      variant="outline-primary"
                                      className="rounded"
                                      size="sm"
                                      onClick={() => handleOpenEditPayment(p, saved)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      className="rounded"
                                      size="sm"
                                      disabled={deletingPaymentId === p.id}
                                      onClick={() => handleDeletePayment(p, saved)}
                                      title="Delete this payment"
                                    >
                                      {deletingPaymentId === p.id ? 'Deleting...' : 'Delete'}
                                    </Button>
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  );
                })}
              </div>
            )}
          </Tab>
        </Tabs>
      )}

      <Modal show={paymentModalCspInfo != null} onHide={handleClosePaymentModal}>
        <Modal.Header className="d-flex align-items-center justify-content-between">
          <Modal.Title className="mb-0">{editingPayment ? 'Edit Payment' : 'New Payment'}</Modal.Title>
          <CloseModalButton onCloseModal={handleClosePaymentModal} />
        </Modal.Header>
        <Modal.Body>
          {paymentModalCspInfo && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Transaction Date</Form.Label>
                <Form.Control
                  type="date"
                  value={paymentForm.transactionDate}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, transactionDate: e.target.value }))
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Payment Type</Form.Label>
                <Form.Control
                  as="select"
                  value={paymentForm.paymentType}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, paymentType: e.target.value }))
                  }
                >
                  <option value="">-- Select Payment Type --</option>
                  {PAYMENT_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                  {paymentForm.paymentType &&
                    !PAYMENT_TYPE_OPTIONS.some((opt) => opt.value === paymentForm.paymentType) && (
                      <option value={paymentForm.paymentType}>
                        {paymentForm.paymentType}
                      </option>
                    )}
                </Form.Control>
              </Form.Group>
              {(['generalSupportAmount', 'sacSupportAmount', 'fee'] as const).map((field) => {
                const label =
                  field === 'generalSupportAmount'
                    ? 'General Support Amount'
                    : field === 'sacSupportAmount'
                      ? 'SAC Support Amount'
                      : 'Fee';
                const amountValue = paymentForm[field];
                const isFocused = paymentModalAmountFocused === field;
                const rawVal = paymentModalAmountRaw[field];
                const hasRaw = rawVal !== undefined && rawVal !== '';
                const showRaw = isFocused && hasRaw && !paymentSubmitting;
                let displayValue: string;
                if (showRaw) {
                  displayValue = rawVal ?? '';
                } else {
                  const numValue =
                    typeof amountValue === 'number'
                      ? amountValue
                      : amountValue !== ''
                        ? Number(amountValue)
                        : null;
                  if (numValue !== null && !Number.isNaN(numValue) && isFinite(numValue)) {
                    displayValue = new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(numValue);
                  } else {
                    displayValue = '';
                  }
                }
                return (
                  <Form.Group key={field} className="mb-3">
                    <Form.Label>{label}</Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="text"
                        value={displayValue}
                        disabled={paymentSubmitting}
                        onFocus={() => {
                          setPaymentModalAmountFocused(field);
                          if (
                            amountValue !== null &&
                            amountValue !== undefined &&
                            amountValue !== '' &&
                            !Number.isNaN(Number(amountValue))
                          ) {
                            const raw =
                              typeof amountValue === 'number'
                                ? amountValue.toFixed(2)
                                : String(amountValue);
                            setPaymentModalAmountRaw((prev) => ({ ...prev, [field]: raw }));
                          } else {
                            setPaymentModalAmountRaw((prev) => ({ ...prev, [field]: '' }));
                          }
                        }}
                        onBlur={() => {
                          setPaymentModalAmountFocused(null);
                          const rawValue = paymentModalAmountRaw[field] ?? '';
                          const numValue = parseCurrency(rawValue);
                          setPaymentForm((prev) => ({
                            ...prev,
                            [field]: numValue !== null && !Number.isNaN(numValue) ? numValue : '',
                          }));
                          setPaymentModalAmountRaw((prev) => {
                            const next = { ...prev };
                            delete next[field];
                            return next;
                          });
                        }}
                        onChange={(e) => {
                          setPaymentModalAmountRaw((prev) => ({
                            ...prev,
                            [field]: e.target.value,
                          }));
                        }}
                        placeholder="0.00"
                        inputMode="decimal"
                      />
                    </InputGroup>
                  </Form.Group>
                );
              })}
              <Form.Group className="mb-3">
                <Form.Label>Reason</Form.Label>
                <Form.Control
                  as="select"
                  value={paymentForm.reasonCode}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, reasonCode: e.target.value }))
                  }
                >
                  <option value="">-- Select Reason --</option>
                  {REASON_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                  {paymentForm.reasonCode &&
                    !REASON_OPTIONS.some((opt) => opt.value === paymentForm.reasonCode) && (
                      <option value={paymentForm.reasonCode}>
                        {paymentForm.reasonCode}
                      </option>
                    )}
                </Form.Control>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClosePaymentModal} disabled={paymentSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={editingPayment ? handleUpdatePaymentSubmit : handleCreatePaymentSubmit}
            disabled={paymentSubmitting}
          >
            {paymentSubmitting
              ? editingPayment
                ? 'Saving...'
                : 'Creating...'
              : editingPayment
                ? 'Save Changes'
                : 'Create Payment'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ApplicationCspInfoTab;
